import React from 'react';
import { am5, am5themes_Animated, am5xy, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { createAxes } from './methods/create-axes';
import { createSeries } from './methods/create-series';
import { addLabels } from '../shared/add-labels';
import { createBaseTooltips } from '../shared/create-base-tooltips';
import { createDownloadButton } from '../shared/create-download-button';
import { handleZoom } from '../shared/handle-zoom';
import { getPaddingLeft } from '../shared/helpers';
import { useChartOnReady } from '../shared/use-chart-on-ready';

export type ChartRef = {
  series: any[] | null;
  seriesKeys: string[];
  onReady: (_callback: (_series: any[]) => void) => void;
};

export type ChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  xAxisField: string;
  numberFormat: string;
  showEndValueLabel: boolean;
  download: boolean;
  yAxisLabel: string | undefined;
  xAxisLabel: string | undefined;
  seriesColorByName: Record<string, string>;
  customDownloadFormatting?: (_data: types.widgets.TableData) => types.widgets.TableData;
  withoutValuePadding: boolean | undefined;
  title?: string;
  dotted?: string[];
  hideLegend?: boolean;
  withGradient?: boolean;
  colorizeBySign?: boolean;
};

const Chart = React.forwardRef<ChartRef, ChartProps>(
  (
    {
      id,
      data,
      fullScreen,
      xAxisField,
      numberFormat,
      showEndValueLabel,
      download,
      yAxisLabel,
      xAxisLabel,
      customDownloadFormatting,
      withoutValuePadding,
      title,
      dotted,
      withGradient = false,
      seriesColorByName,
      colorizeBySign,
    },
    ref
  ) => {
    const widgetId = `chart-${id}-${fullScreen ? 'fs' : 'sw'}`;
    const theme = mui.styles.useTheme() as mui.core.Theme;
    const seriesRef = React.useRef<any>(null);
    const seriesKeys = Object.keys(data?.[0] ?? {}).filter((key) => key !== xAxisField);
    const [seriesState, setSeriesState] = React.useState<any[] | null>(null);
    const { registerCallback, executeCallbacks } = useChartOnReady();

    React.useImperativeHandle(
      ref,
      () => ({
        series: seriesState,
        seriesKeys,
        onReady: registerCallback,
      }),
      [seriesState, seriesKeys, registerCallback]
    );

    // Calculate if dataset is large for performance optimizations
    const allKeys = Object.keys(data?.[0] ?? {});
    const seriesCount = allKeys.length - 1;
    const totalDataPoints = data.length * seriesCount;
    const isLargeDataset = totalDataPoints > 10_000;

    React.useEffect(() => {
      const root = am5.Root.new(widgetId);
      root._logo?.dispose();

      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelX: 'panX',
          wheelY: 'zoomX',
          pinchZoomX: false,
          layout: root.verticalLayout,
          maxTooltipDistance: 0,
          paddingLeft: getPaddingLeft(download, yAxisLabel),
        })
      );

      // Disable animations for faster initial load with large datasets
      if (!isLargeDataset) {
        root.setThemes([am5themes_Animated.new(root)]);
        chart.appear(1000, 100);
      } else {
        chart.appear(0, 0);
      }

      if (title) {
        chart.children.unshift(
          am5.Label.new(root, {
            text: title,
            fontSize: 15,
            x: am5.p50,
            centerX: am5.p50,
            paddingTop: 0,
            paddingBottom: 0,
            marginBottom: 20,
          })
        );
      }

      root.numberFormatter.setAll({
        numberFormat,
        numericFields: ['valueY'],
      });

      const { xAxis, yAxis } = createAxes({
        root,
        chart,
        showEndValueLabel,
        withoutValuePadding,
        dataLength: data.length,
        isLargeDataset,
      });

      xAxis.get('renderer').setAll({
        minGridDistance: 50,
        cellStartLocation: 0,
        cellEndLocation: 1,
      });

      xAxis.setAll({
        extraMax: 0,
        extraMin: 0,
        strictMinMax: true,
        maxDeviation: 0,
      });

      xAxis.get('renderer').grid.template.setAll({ visible: false });

      const createdSeries = createSeries({
        root,
        chart,
        data,
        xAxisField,
        xAxisRenderer: xAxis,
        yAxisRenderer: yAxis,
        showEndValueLabel,
        dotted,
        seriesColorByName,
        colorizeBySign,
        isLargeDataset,
      });

      if (withGradient) {
        chart.series.each((s: any) => {
          if (s?.fills?.template) {
            s.fills.template.setAll({ visible: true, fillOpacity: 0.18 });
            s.set(
              'fillGradient',
              am5.LinearGradient.new(root, {
                rotation: 90,
                stops: [
                  { color: (s.get('stroke') as any) ?? am5.color(0x000000), opacity: 0.18 },
                  { color: (s.get('stroke') as any) ?? am5.color(0x000000), opacity: 0 },
                ],
              })
            );
            const zeroRange = yAxis.createAxisRange(yAxis.makeDataItem({ value: 0 }));
            s.set('fillTo', zeroRange);
          }
        });
      }

      seriesRef.current = createdSeries;
      setSeriesState(createdSeries);

      createBaseTooltips({ root, chart, xAxis, yAxis, theme, createdSeries });
      handleZoom(chart);
      if (download) createDownloadButton(root, xAxisField, data, customDownloadFormatting);
      addLabels({ root, yAxis, xAxis, yAxisLabel, xAxisLabel });

      // Execute callbacks when chart has actually finished rendering and animating
      executeCallbacks(createdSeries);

      return () => {
        setSeriesState(null);
        root.dispose();
      };
    }, [data, withGradient, seriesColorByName]);

    return (
      <mui.core.Box
        sx={{
          width: '100%',
          height: '100%',
        }}
      >
        <div
          id={widgetId}
          style={{
            width: '100%',
            height: '100%',
          }}
        />
      </mui.core.Box>
    );
  }
);

export default Chart;
